import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useScrollLock } from "../../hooks/useScrollLock";
import {
  ModalOverlay,
  ModalContainer,
  ModalContent,
  ModalTitle,
  TitleContainer,
  ReferralLinkContainer,
  ReferralLink,
  CopyButton,
  ModalSubtitle,
  LightText,
  CloseButton,
} from "./ReferralModal.styles";
import { selectUser } from "../../store/slices/user/slice";

import { toast } from "../modern-ui/sonner";
import Button from "../utils/Button/Button";

import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
import { ReactComponent as CopySvg } from "../../assets/icons/copy.svg";
import { ReactComponent as SuccessSvg } from "../../assets/icons/success.svg";

const ReferralModal = ({ isOpen, onClose }) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const [copySuccess, setCopySuccess] = useState(false);

  const user = useSelector(selectUser);
  const referralLink = user?.affiliateLink;

  useScrollLock(shouldRender);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      toast.success("Referral link copied to clipboard!");
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      toast.error("Failed to copy link. Please try again.");
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: "Join Playbox - NSFW AI Video Generation",
      text: "Create amazing NSFW AI videos with Playbox! Use my referral link to get started.",
      url: referralLink,
    };

    try {
      // Check if Web Share API is supported (mainly on mobile)
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success("Invite shared successfully!");
      } else {
        // Fallback: Copy to clipboard if Web Share API is not supported
        await navigator.clipboard.writeText(
          `${shareData.text} ${shareData.url}`
        );
        toast.success("Invite message copied to clipboard!");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        // User didn't cancel the share
        console.error("Error sharing:", err);
        toast.error("Failed to share invite. Please try again.");
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsAnimating(false);
      const timer = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      return () => cancelAnimationFrame(timer);
    } else if (shouldRender) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  if (!shouldRender) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick} $isAnimating={isAnimating}>
      <ModalContainer $isAnimating={isAnimating}>
        <ModalContent>
          <TitleContainer>
            <ModalTitle>Earn from Your Network!</ModalTitle>
            <CloseButton onClick={onClose}>
              <CloseSvg />
            </CloseButton>
          </TitleContainer>
          <ModalSubtitle>
            Invite users to Playbox and receive 35% of their lifetime spend.
          </ModalSubtitle>

          <ReferralLinkContainer>
            <ReferralLink>
              <span>{referralLink?.replace("https://www.", "")}</span>
            </ReferralLink>
            <CopyButton onClick={handleCopyLink} $success={copySuccess}>
              {copySuccess ? <SuccessSvg /> : <CopySvg />}
            </CopyButton>
          </ReferralLinkContainer>
          <LightText>or</LightText>
          <Button
            style={{ width: "100%" }}
            onClick={handleShare}
            title={"Invite Friends"}
          />
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ReferralModal;
